import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Database, Info, RefreshCw, MousePointerClick,
  Send, ShoppingCart, CreditCard, CheckCircle2, XCircle,
  IndianRupee, Users2, ArrowUpRight, ExternalLink,
} from "lucide-react";

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

const relative = (s) => {
  if (!s) return "—";
  const diff = (new Date(s) - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const dir = diff >= 0 ? "in" : "ago";
  if (abs < 60) return `${Math.round(abs)}s ${dir}`;
  if (abs < 3600) return `${Math.round(abs / 60)}m ${dir}`;
  if (abs < 86400) return `${Math.round(abs / 3600)}h ${dir}`;
  return `${Math.round(abs / 86400)}d ${dir}`;
};

const Kpi = ({ icon: Icon, label, value, valuePrefix = "", tint = "slate" }) => {
  const map = {
    slate: "bg-slate-50 text-slate-900 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-900 border-indigo-200",
    orange: "bg-orange-50 text-orange-900 border-orange-200",
    rose: "bg-rose-50 text-rose-900 border-rose-200",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tint] || map.slate}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-70 flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-heading text-2xl font-extrabold tabular-nums">
        {valuePrefix}{typeof value === "number" ? INR.format(value) : value}
      </div>
    </div>
  );
};

// Individual funnel step chip inside a row — dims to slate when 0.
const Step = ({ n, label, tint = "indigo" }) => (
  <div className="flex flex-col items-center gap-0.5 min-w-[60px]" title={label}>
    <div
      className={`w-full text-center text-xs font-bold tabular-nums rounded px-1 py-0.5 ${
        n > 0
          ? tint === "emerald" ? "bg-emerald-100 text-emerald-800"
          : tint === "rose"    ? "bg-rose-100 text-rose-800"
          : "bg-indigo-100 text-indigo-800"
          : "bg-slate-100 text-slate-400"
      }`}
    >{n}</div>
    <div className="text-[9px] uppercase tracking-widest text-slate-500 truncate max-w-[64px]">
      {label}
    </div>
  </div>
);

export default function PromoAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [recent, setRecent] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState("net_revenue");
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [funnel, recentRes] = await Promise.all([
        api.get("/analytics/promo-funnel"),
        api.get("/analytics/subscription-links/recent", { params: { limit: 20 } }),
      ]);
      setRows(funnel.data.rows || []);
      setTotals(funnel.data.totals || {});
      setRecent(recentRes.data.links || []);
    } catch {
      toast.error("Failed to load promo analytics");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/analytics/reconcile-now");
      toast.success(`Reconciled ${data.reconciled ?? 0} · pulled ${data.pulled ?? 0} · skipped ${data.skipped_no_user ?? 0}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally { setReconciling(false); }
  };

  const visible = useMemo(() => {
    let list = rows.slice();
    if (!showInactive) list = list.filter((r) => r.active);
    list.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    return list;
  }, [rows, showInactive, sortKey]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="promo-analytics-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Marketing</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-orange-500" /> Promo Analytics
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            End-to-end funnel per promo code — from personalised links Connect
            has sent, through the subscription page, all the way to reconciled
            payments and revenue. Zero-count columns light up as the sub-page
            webhook (Phase 4) starts flowing events.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="promo-analytics-reconcile-btn"
            onClick={runReconcile}
            disabled={reconciling}
            className="inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white text-xs font-semibold px-3 py-2 rounded-md"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${reconciling ? "animate-spin" : ""}`} />
            {reconciling ? "Reconciling…" : "Reconcile now"}
          </button>
          <button
            data-testid="promo-analytics-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-xs font-semibold text-slate-700 px-3 py-2 rounded-md"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-6" data-testid="promo-analytics-kpis">
        <Kpi icon={Send} label="Links generated" value={totals.links_generated || 0} tint="indigo" />
        <Kpi icon={MousePointerClick} label="Links sent" value={totals.links_sent || 0} tint="indigo" />
        <Kpi icon={Users2} label="Unique users" value={totals.unique_users || 0} tint="slate" />
        <Kpi icon={CheckCircle2} label="Payments" value={totals.payment_success || 0} tint="emerald" />
        <Kpi icon={IndianRupee} label="Net revenue" value={totals.net_revenue || 0} valuePrefix="₹" tint="emerald" />
        <Kpi icon={IndianRupee} label="Discount given" value={totals.discount_amount_total || 0} valuePrefix="₹" tint="orange" />
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-start gap-2 mb-4">
        <Info className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
        <div className="text-xs text-indigo-900 leading-relaxed">
          Payment counts come from the reconciled MariaDB <code>subscriptions</code> table
          (Phase 2). Intermediate steps like <b>Page Opened</b>, <b>Promo Applied</b>, <b>Pay Now</b>,
          <b> Razorpay Opened</b> stay at 0 until the subscription page starts emitting
          events to Connect&apos;s webhook (Phase 4 — dev in progress).
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Sort by</label>
          <select
            data-testid="promo-analytics-sort"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="px-2 py-1 text-xs border border-slate-200 rounded-md bg-white"
          >
            <option value="net_revenue">Net revenue</option>
            <option value="payment_success">Payments</option>
            <option value="links_sent">Links sent</option>
            <option value="conversion_rate">Conversion rate</option>
          </select>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-2">
          <input
            type="checkbox"
            data-testid="promo-analytics-show-inactive"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive promos
        </label>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-10">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500" data-testid="promo-analytics-empty">
          No promo activity yet. Sync promos from the Subscription MariaDB and
          send a link to see the funnel light up.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="promo-analytics-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-3">Promo</th>
                <th className="text-left px-3 py-3">Discount</th>
                <th className="text-left px-3 py-3">Funnel</th>
                <th className="text-right px-3 py-3">Payments</th>
                <th className="text-right px-3 py-3">Gross</th>
                <th className="text-right px-3 py-3">Discount</th>
                <th className="text-right px-3 py-3">Net</th>
                <th className="text-right px-3 py-3">Conv %</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.promo_code}
                    data-testid={`promo-row-${r.promo_code}`}
                    onClick={() => navigate(`/users?promo=${encodeURIComponent(r.promo_code)}`)}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-orange-50/40 transition-colors ${!r.active ? "opacity-60" : ""}`}
                    title={`View users who received ${r.promo_code}`}
                >
                  <td className="px-3 py-3 align-top">
                    <div className="font-mono font-bold text-orange-700">{r.promo_code}</div>
                    <div className="mt-0.5 flex items-center gap-1">
                      <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                        <Database className="h-2.5 w-2.5" /> MariaDB
                      </span>
                      {!r.active && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-slate-100 text-slate-500 border-slate-200">
                          Inactive
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    {r.discount_pct ? (
                      <span className="text-emerald-700 font-semibold">{r.discount_pct}%</span>
                    ) : r.discount_amount ? (
                      <span className="text-emerald-700 font-semibold">₹{INR.format(r.discount_amount)}</span>
                    ) : <span className="text-slate-400">—</span>}
                    {r.exam_restriction && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{r.exam_restriction}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex items-center gap-1.5">
                      <Step n={r.links_sent}         label="Sent" />
                      <Step n={r.page_opened}        label="Opened" />
                      <Step n={r.plan_selected}      label="Plan" />
                      <Step n={r.promo_applied}      label="Applied" />
                      <Step n={r.pay_now_clicked}    label="Pay Now" />
                      <Step n={r.razorpay_opened}    label="Razorpay" />
                      <Step n={r.payment_success}    label="Paid" tint="emerald" />
                      <Step n={r.payment_failed + r.payment_cancelled} label="Lost" tint="rose" />
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums align-top">
                    {r.payment_success || 0}
                    {r.subscriptions_created > r.payment_success && (
                      <div className="text-[10px] text-slate-500">{r.subscriptions_created} subs</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums align-top">
                    {r.gross_amount ? `₹${INR.format(r.gross_amount)}` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-orange-700 align-top">
                    {r.discount_amount_total ? `−₹${INR.format(r.discount_amount_total)}` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-700 align-top">
                    {r.net_revenue ? `₹${INR.format(r.net_revenue)}` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums align-top">
                    {r.conversion_rate ? `${(r.conversion_rate * 100).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent activity strip */}
      {recent.length > 0 && (
        <div className="mt-8" data-testid="promo-analytics-recent">
          <h2 className="font-heading text-lg font-extrabold mb-3 flex items-center gap-2">
            <ArrowUpRight className="h-4 w-4 text-orange-500" /> Recent link activity
          </h2>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Promo</th>
                  <th className="text-left px-3 py-2">Channel</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Sent</th>
                  <th className="text-left px-3 py-2">Cadence</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.trial_user_name || r.trial_user_id?.slice(0, 8)}</div>
                      <div className="text-[10px] text-slate-500">{r.trial_user_email}</div>
                    </td>
                    <td className="px-3 py-2 font-mono">{r.promo_code || "—"}</td>
                    <td className="px-3 py-2 uppercase text-[10px]">{r.channel || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                        r.status === "purchased" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : r.status === "opened"  ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                        : r.status === "sent"    ? "bg-slate-100 text-slate-700 border-slate-200"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{relative(r.sent_at || r.created_at)}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono">{r.cadence_id ? r.cadence_id.slice(0, 6) + "…" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
